import { ComponentPropsWithoutRef, ComponentType, FC, PropsWithChildren } from 'react';
import styled from 'styled-components';

import {
  displayMixins,
  DisplaySize,
  fontFamilyMixin,
  headingMixins,
  HeadingSize,
  paragraphMixins,
  ParagraphMixinSize,
  ParagraphSize,
} from 'design-system/mixins/text';
import { TextColors } from 'design-system/theme';

export interface SpanProps {
  id?: string;
  color?: keyof TextColors | null;
  'data-test'?: string;
}

const StyledSpan: FC<PropsWithChildren<SpanProps>> = ({ children, color, ...othersThanColor }) => (
  <span {...othersThanColor}>{children}</span>
);

/**
 * Base class of all Text components.
 */
export const Span = styled(StyledSpan)`
  color: ${({ color = 'primary', theme }) => (color == null ? 'inherit' : theme.color.text[color])};
  ${fontFamilyMixin};
`;

interface DisplayProps {
  size: DisplaySize;
}

export const Display = styled(Span).attrs(() => ({ as: 'h1' }))<DisplayProps>`
  ${({ size }) => displayMixins[size]}
`;

export interface HeadingProps
  extends Omit<ComponentPropsWithoutRef<'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'>, 'color'>,
    SpanProps {
  size: HeadingSize;
  as?: string | ComponentType<any>;
}

const HeadingComponent = styled(Span)<HeadingProps>`
  ${({ size }) => headingMixins[size]}
`;

// Needs a wrapper to support chaining .attrs without overwriting `as: size`
// This is a bug with styled components when chaining .attrs where the inner
// .attr cannot access values passed by the outer .attr.
export const Heading: FC<HeadingProps> = ({ as, size, ...props }) => {
  return <HeadingComponent as={as || 'h3'} size={size} {...props} />;
};

export interface ParagraphProps extends Omit<ComponentPropsWithoutRef<'p'>, 'color'>, SpanProps {
  size?: ParagraphSize;
  weight?: 'bold' | 'normal' | 'light';
  as?: string | ComponentType<any>;
}

export const Paragraph = styled(Span).attrs<ParagraphProps>(({ as = 'p' }) => ({
  as,
}))<ParagraphProps>`
  ${({ size = 'md', weight = 'normal' }) => {
    if (weight !== 'normal') {
      if (['md', 'lg'].includes(size)) {
        return paragraphMixins[
          (size + weight.charAt(0).toUpperCase() + weight.slice(1)) as ParagraphMixinSize
        ];
      } else {
        console.error(`Weight '${weight}' does not exist for size '${size}'.`);
      }
    }

    return paragraphMixins[size];
  }}
  /* Unlike the other text components, Paragraph defaults to the secondary text color. */
  color: ${({ color = 'secondary', theme }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return theme.color.text[color];
  }};
`;

export interface LabelProps extends Omit<ComponentPropsWithoutRef<'label'>, 'color'>, SpanProps {
  size?: ParagraphSize;
  weight?: 'bold' | 'normal' | 'light';
  'data-test'?: string;
}

export const Label = styled(Paragraph).attrs<LabelProps>({
  as: 'label',
})<LabelProps>`
  color: ${({ color = 'primary', theme }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return theme.color.text[color];
  }};
`;
